import $ from 'jquery';

import CheckoutSectionView from 'chairisher/view/checkout/checkoutsection';
import CountryView from 'chairisher/view/account/countries';
import FormFieldComponent from 'chairisher/component/form/field';

import { isAddressAutocompleteEnabled } from 'chairisher/context/google';
import { trackShippingAddressSubmitError, trackShippingAddressSubmitSuccess } from 'chairisher/analytics/checkout';
import { getPurchaseObject, getSavedShippingAddress, setPurchaseObject } from 'chairisher/context/purchase';
import { handleAsyncError } from 'chairisher/util/logging';
import { isTouchDevice } from 'chairisher/util/mediaquery';

const fieldNameToSummarySelectorMap = {
    city: '.js-address-city',
    country_name: '.js-address-country',
    email: '.js-email',
    name: '.js-address-name',
    phone_number: '.js-address-phone-number',
    postal_code: '.js-address-zip',
    state: '.js-address-state',
    street_address_line_1: '.js-address-line1',
    street_address_line_2: '.js-address-line2',
};

/**
 * Manages and displays shipping address data.
 * This view contains a shipping address form, and two lists of saved shipping addresses.
 * The first address list displays a few of the most recently created.
 * The second address list is accessed from a modal and displays all.
 */
class CheckoutShippingAddressView extends CheckoutSectionView {
    constructor(selector) {
        super(selector);
        this.shouldCalculateProcessingFee = false;
    }

    /**
     * @inheritDoc
     */
    bind() {
        const $addressFields = this.$fieldsEl.find('.js-address-fields');

        if (isAddressAutocompleteEnabled()) {
            import('chairisher/component/autocomplete/google/address').then(
                ({ default: GoogleAutocompleteAddress }) => {
                    const googleAutocompleteAddress = new GoogleAutocompleteAddress({ $addressFields });
                    googleAutocompleteAddress.bind();
                },
                handleAsyncError,
            );
        }
        const countryView = new CountryView({
            $countryEl: $addressFields.find('.js-address-country'),
            shouldLimitToShippableStates: true,
        });
        countryView.bind();
        countryView.updateForCurrentCountry().done(() => {
            this.renderForm();
        });

        // First address list - contains shortened list of addresses.
        const $savedAddresses = this.$fieldsEl.find('.js-saved-addresses');
        // Second address list - contains all addresses
        const $shippingAddressModal = $('.js-address-modal');

        const $addressModalSubmitBtn = $shippingAddressModal.find('.js-address-submit-btn');
        const $createNewAddressLink = this.$fieldsEl.find('.js-create-new-address-link');
        const $showAddressModalLink = this.$fieldsEl.find('.js-show-address-modal-link');
        const $showSavedAddressesLink = this.$fieldsEl.find('.js-saved-address-link');

        const numAddressesShown = $savedAddresses.find('li').length;

        // Closure that returns the selected account address ID from the given address list selector.
        const getCheckedAccountAddressId = ($addressListSelector) =>
            parseInt($addressListSelector.find('[name="address"]:checked').val(), 10);

        const initialAccountAddressId = getCheckedAccountAddressId($savedAddresses);

        // Closure that sets the checked address in one of the address lists.
        const selectAccountAddress = ($selector, accountAddressId) => {
            $selector.find(`[value="${accountAddressId}"]`).prop('checked', true);
            $selector.find(`[value!="${accountAddressId}"]`).prop('checked', false);
        };

        if (initialAccountAddressId) {
            // Check same address in modal and enable button.
            selectAccountAddress($shippingAddressModal, initialAccountAddressId);
            $addressModalSubmitBtn.prop('disabled', false);
        }

        // Closure for showing/hiding the saved address UI when the relevant links are clicked.
        const toggleSavedAddressView = (shouldShow) => {
            [$addressFields, $showSavedAddressesLink].forEach(($selector) => {
                $selector.toggleClass('hidden', shouldShow);
            });
            [$createNewAddressLink, $savedAddresses, $showAddressModalLink].forEach(($selector) => {
                $selector.toggleClass('hidden', !shouldShow);
            });
        };

        // Closure for updating data in purchase context after a user selects a saved address.
        const updateAddress = (accountAddress, shouldRenderErrors = true) => {
            // Disable the next button to ensure address is updated before progression
            this.toggleNextButtonDisabled(true);

            const updatePurchaseContext = () => {
                const purchaseObject = getPurchaseObject();
                purchaseObject.shipto_address = accountAddress.address;
                purchaseObject.shipto_address.name = accountAddress.name;
                purchaseObject.account.phone_number = accountAddress.phone_number;
                setPurchaseObject(purchaseObject);
                this.populateFieldNameToComponentMap();
                this.checkoutCallbacks
                    .updateEditableView(shouldRenderErrors)
                    .done(() => {
                        if (!this.isValid()) {
                            // The saved address view doesn't display field level errors.
                            // So, if the saved address is invalid for some reason, then show the form view.
                            toggleSavedAddressView(false);
                        }
                    })
                    .always(() => this.toggleNextButtonDisabled(false));
            };

            const newCountryCode = accountAddress.address.country_code.value;

            if (newCountryCode === countryView.getCountryCode()) {
                updatePurchaseContext();
            } else {
                // The form needs to be updated when the country changes
                countryView
                    .updateForCountry(newCountryCode, countryView.$countryEl)
                    .done(() => updatePurchaseContext());
            }

            selectAccountAddress($shippingAddressModal, accountAddress.id.value);
            selectAccountAddress($savedAddresses, accountAddress.id.value);
        };

        // Shows saved address UI after user clicks 'show addresses' from the form.
        $showSavedAddressesLink.on('click', () => {
            $shippingAddressModal.find('input').prop('checked', false);
            $savedAddresses.find('input').prop('checked', false);
            $addressModalSubmitBtn.prop('disabled', true);
            toggleSavedAddressView(true);
        });

        // Shows form after user clicks the 'add new address link' from the saved address UI.
        $createNewAddressLink.on('click', () => {
            const checkedAddressId = getCheckedAccountAddressId($savedAddresses);
            if (checkedAddressId) {
                const selectedAccountAddress = { ...getSavedShippingAddress(checkedAddressId) };

                // Clear the address fields, but leave the account fields and country_code intact.
                const selectedAddress = { ...selectedAccountAddress.address };
                selectedAddress.street_address_line_1 = { value: '' };
                selectedAddress.street_address_line_2 = { value: '' };
                selectedAddress.city = { value: '' };
                selectedAddress.state = { value: '' };
                selectedAddress.postal_code = { value: '' };
                selectedAccountAddress.address = selectedAddress;

                // Don't render errors as it's in invalid state now.
                updateAddress(selectedAccountAddress, false);
            }
            toggleSavedAddressView(false);
            if (!isTouchDevice()) {
                // Set focus to first empty field
                this.$fieldsEl
                    .find('input')
                    .not('[name="street_address_line_2"]')
                    .filter((_, inputEl) => inputEl.value === '')
                    .first()
                    .focus();
            }
        });

        $savedAddresses.on('click', 'input', ({ target: { value } }) => {
            updateAddress(getSavedShippingAddress(parseInt(value, 10)));
        });

        $showAddressModalLink.on('click', () => {
            $shippingAddressModal.modal('show');
        });

        $shippingAddressModal.find('input').on('click', () => {
            $addressModalSubmitBtn.prop('disabled', false);
        });

        $addressModalSubmitBtn.on('click', () => {
            const accountAddressId = getCheckedAccountAddressId($shippingAddressModal);
            if ($savedAddresses.find(`[value="${accountAddressId}"]`).length === 0) {
                // If the selected address from the modal isn't visible in the shorter address list,
                // then recreate the <ul> so that it can show the new selection.
                const $newAddressList = $('<ul>', {
                    class: $savedAddresses.find('ul').attr('class'),
                });
                const $selectedItem = $shippingAddressModal.find(`li:has([value="${accountAddressId}"])`).clone();
                $newAddressList.append($selectedItem);
                $newAddressList.append(
                    $shippingAddressModal
                        .find('li')
                        .clone()
                        .slice(0, numAddressesShown - 1),
                );
                $savedAddresses.find('ul').replaceWith($newAddressList);
            }
            $shippingAddressModal.modal('hide');
            updateAddress(getSavedShippingAddress(accountAddressId));
        });
    }

    /**
     * @inheritDoc
     */
    getDataFromContext() {
        const purchaseObject = getPurchaseObject();
        return { ...purchaseObject.account, ...purchaseObject.shipto_address };
    }

    /**
     * @inheritDoc
     */
    populateFieldNameToComponentMap() {
        const data = this.getDataFromContext();
        Object.entries(data).forEach(([key, value]) => {
            const component = new FormFieldComponent({
                $el: this.$el.find(`[name="${key}"]`),
                name: key,
                data: value || { value: null },
            });
            this.fieldNameToComponentMap[key] = component;
            component.render();
        });
    }

    /**
     * @inheritdoc
     */
    renderSummary() {
        Object.keys(this.fieldNameToComponentMap).forEach((key) => {
            const component = this.fieldNameToComponentMap[key];
            this.$summaryEl.find(fieldNameToSummarySelectorMap[key]).html(component.getDisplayValue());
        });
    }

    /**
     * @inheritDoc
     */
    getValue() {
        let result;
        const data = super.getValue();

        if (data) {
            result = {
                account: {},
                shipto_address: {},
            };
            Object.keys(data).forEach((key) => {
                const value = data[key];
                if (key === 'email') {
                    result.account.email = value;
                } else if (key === 'is_opted_in') {
                    result.account.is_opted_in = {
                        value: this.$fieldsEl.find('[name="is_opted_in"]').is(':checked'),
                    };
                } else {
                    result.shipto_address[key] = value;
                }
            });
        }
        return result;
    }

    /**
     * @inheritDoc
     */
    submitErrorAnalytics() {
        trackShippingAddressSubmitError();
    }

    /**
     * @inheritDoc
     */
    submitSuccessAnalytics() {
        trackShippingAddressSubmitSuccess();
    }
}

export default CheckoutShippingAddressView;
